import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Preserve selection order and repeats; the last nonempty selection is current. */
export function purchaseReasonHistory(reasons?: string | null): string[] {
  return (reasons ?? '')
    .split(',')
    .map((reason) => reason.trim())
    .filter(Boolean);
}

export const PURCHASE_REASONS = [
  'themes',
  'organizer_tools',
  'personal_address',
  'no_ads',
  'bundle',
  'support',
  'other',
] as const;
const knownReasons = new Set<string>(PURCHASE_REASONS);

const reasonIcons: Record<string, string> = {
  themes: '🎨',
  organizer_tools: '🛠️',
  personal_address: '🔗',
  no_ads: '🚫',
  bundle: '🎁',
  support: '❤️',
  other: '💬',
};

export function PurchaseReasonIcon({ reason }: { reason: string }) {
  return <span aria-hidden="true">{reasonIcons[reason] ?? reasonIcons.other}</span>;
}

export function PaymentReasons({
  reasons,
  comment,
}: {
  reasons?: string | null;
  comment?: string | null;
}) {
  const { t } = useTranslation();
  const history = purchaseReasonHistory(reasons);
  const latest = history.at(-1);
  const color = latest && knownReasons.has(latest) ? latest : 'other';
  const label = (reason: string) =>
    knownReasons.has(reason) ? t(`admin.payments.reasonLabels.${reason}`) : reason;
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      if (!trigger.current || !popup.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const sheet = popup.current.getBoundingClientRect();
      const left = Math.max(
        16,
        Math.min(anchor.right - sheet.width, window.innerWidth - sheet.width - 16),
      );
      const below = anchor.bottom + 8;
      const top =
        below + sheet.height <= window.innerHeight - 16
          ? below
          : Math.max(16, anchor.top - sheet.height - 8);
      popup.current.style.left = `${left}px`;
      popup.current.style.top = `${top}px`;
    };
    position();
    const observer = new ResizeObserver(position);
    if (popup.current) observer.observe(popup.current);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [open]);

  if (!latest && !comment?.trim()) return <span className="admin-payments__no-survey">—</span>;
  const currentLabel = latest ? label(latest) : t('admin.payments.commentOnly');
  const commentCharacters = Array.from(comment?.trim() ?? '');
  const commentPreview = commentCharacters.length > 150
    ? `${commentCharacters.slice(0, 149).join('')}…`
    : commentCharacters.join('');
  return (
    <>
      <button
        ref={trigger}
        type="button"
        popoverTarget={id}
        className={`admin-payments__reason admin-payments__reason--${color}`}
        aria-label={t('admin.payments.viewSurvey', { reason: currentLabel })}
        aria-haspopup="dialog"
        aria-controls={id}
        aria-expanded={open}
      >
        <PurchaseReasonIcon reason={color} /> {currentLabel}
      </button>
      {commentPreview && <p className="admin-payments__comment-preview">{commentPreview}</p>}
      <div
        ref={popup}
        id={id}
        popover="auto"
        role="dialog"
        aria-labelledby={`${id}-title`}
        className="admin-payments__survey"
        onToggle={(event) => setOpen(event.newState === 'open')}
      >
        <div className="admin-payments__survey-heading">
          <h3 id={`${id}-title`}>{t('admin.payments.surveyTitle')}</h3>
          <button
            type="button"
            aria-label={t('admin.payments.closeSurvey')}
            onClick={() => {
              popup.current?.hidePopover();
              trigger.current?.focus();
            }}
          >
            ×
          </button>
        </div>
        {history.length > 1 && (
          <>
            <h4>{t('admin.payments.reasonHistory')}</h4>
            <ol>
              {history.slice(0, -1).map((reason, index) => (
                <li key={index}>
                  <span
                    className={`admin-payments__reason admin-payments__reason--${knownReasons.has(reason) ? reason : 'other'}`}
                  >
                    <PurchaseReasonIcon reason={reason} /> {label(reason)}
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
        <h4>{t('admin.payments.finalSelection')}</h4>
        {latest ? (
          <span className={`admin-payments__reason admin-payments__reason--${color}`}>
            <PurchaseReasonIcon reason={latest} /> {label(latest)}
          </span>
        ) : (
          <p className="admin-payments__survey-empty">{t('admin.payments.noReasons')}</p>
        )}
        <h4>{t('admin.payments.surveyComment')}</h4>
        <p
          className={
            comment?.trim() ? 'admin-payments__survey-comment' : 'admin-payments__survey-empty'
          }
        >
          {comment?.trim() || t('admin.payments.noSurveyComment')}
        </p>
      </div>
    </>
  );
}
